import type { ReviewableOrderResponse } from '@contracts/order/ReviewableOrderResponse';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';

import type { FeesFormFields } from '../schemas/ReviewOrderFormPermitFields';
import {
  assembleAdditionalFeesRequest,
  assemblePermitFeesRequest,
} from './OrdersClient';
import { assembleReviewableOrder } from './ReviewableOrderAssembler';

export const OrderInvoicesClient = {
  updateAdminFeeRate: async ({
    orderId,
    adminFeeRate,
  }: {
    orderId: string;
    adminFeeRate: number;
  }): Promise<ReviewableOrder> => {
    const { data: response } =
      await AuthenticatedClient.post<ReviewableOrderResponse>(
        `order-invoices/${orderId}:updateAdminFeeRate`,
        {
          adminFeeRate,
        },
      );
    return assembleReviewableOrder(response);
  },
  generateInvoice: async ({
    orderId,
    billingDate,
  }: {
    orderId: string;
    billingDate?: string;
  }): Promise<void> => {
    await AuthenticatedClient.post<ReviewableOrderResponse>(
      `order-invoices/${orderId}:generateInvoice`,
      {
        billingDate,
      },
    );
  },
  modifyInvoice: async ({ orderId }: { orderId: string }): Promise<void> => {
    await AuthenticatedClient.put<ReviewableOrderResponse>(
      `order-invoices/${orderId}:modifyInvoice`,
    );
  },
  editInvoice: async ({ orderId }: { orderId: string }): Promise<void> => {
    await AuthenticatedClient.post<ReviewableOrderResponse>(
      `order-invoices/${orderId}:editInvoice`,
    );
  },
  deleteInvoice: async ({ orderId }: { orderId: string }): Promise<void> => {
    await AuthenticatedClient.post<ReviewableOrderResponse>(
      `order-invoices/${orderId}:deleteInvoice`,
    );
  },
  sendInvoiceEmail: async ({
    orderId,
    fields,
  }: {
    orderId: string;
    fields: FeesFormFields;
  }): Promise<{ customerEmails: string[] }> => {
    const permitFeesAssembled = assemblePermitFeesRequest(fields.permitFees);
    const additionalFees = assembleAdditionalFeesRequest(fields.additionalFees);
    const { data: response } = await AuthenticatedClient.post<{
      customerEmails: string[];
    }>(`order-invoices/${orderId}:sendInvoiceEmail`, {
      permitFees: permitFeesAssembled,
      additionalFees,
    });
    return response;
  },
};
